/**
 * sucursal-search-filter.js — Dislicores
 *
 * Oculta del buscador predictivo los productos que no tienen stock en la
 * sucursal seleccionada.
 *
 * Por que un archivo aparte y no sucursal-card-stock.js: ahi el comportamiento
 * correcto es MARCAR la card como "¡Pronto de vuelta!" (el usuario ya esta
 * navegando una coleccion y conviene que vea el producto). En las sugerencias
 * del buscador el requerimiento es el contrario: si no esta disponible en la
 * sucursal, no debe aparecer.
 *
 * Reusa el mismo endpoint de inventario:
 *   GET /poligonos/inventory?locationId=<id>&productIds=<id1,id2,...>
 *   -> [{ productId|id, handle, variants: [{ variantId, available }] }]
 *
 * Fail-open deliberado: si no hay sucursal, si el fetch falla, o si el endpoint
 * no devuelve un producto, la card se deja visible. Es preferible mostrar de mas
 * a vaciar el buscador por un error de red.
 */
(function () {
  'use strict';

  if (window.__sucursalSearchFilterReady) return;
  window.__sucursalSearchFilterReady = true;

  var STORAGE_KEY = 'sucursal_store';
  var ENDPOINT = 'https://sioconn.dislicores.com/poligonos/inventory';

  // Solo los resultados reales de busqueda. Las recomendaciones estaticas
  // (search__products-recommendation) las sigue tratando card-stock marcandolas.
  var RESULTS_SELECTOR = '#predictive-search-results-products-list';
  var CARD_SELECTOR = '.product-card[data-product-id]';
  var HIDDEN_ATTR = 'data-sucursal-hidden';

  var requestCounter = 0;
  var refreshTimer = null;

  // Cache por sucursal: el buscador repite mucho los mismos productos entre
  // pulsaciones, y sin esto cada tecla dispara una consulta redundante.
  var cache = { locationId: '', products: {} };

  function getState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function normalizeId(value) {
    if (!value) return '';
    return String(value).split('/').pop();
  }

  function isGiftCard(card) {
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return false;

    return /\/products\/tarjetas-de-regalo(?:[/?#]|$)/i.test(link.getAttribute('href') || '');
  }

  function getResultCards() {
    var containers = document.querySelectorAll(RESULTS_SELECTOR);
    var cards = [];

    Array.prototype.forEach.call(containers, function (container) {
      Array.prototype.forEach.call(container.querySelectorAll(CARD_SELECTOR), function (card) {
        if (!isGiftCard(card)) cards.push(card);
      });
    });

    return cards;
  }

  // Se oculta la columna contenedora (.f-column) para no dejar un hueco en la
  // grilla; si la card no vive en una columna, se oculta la card misma.
  function getHideTarget(card) {
    return card.closest('.f-column') || card;
  }

  function hideCard(card) {
    var target = getHideTarget(card);
    target.setAttribute(HIDDEN_ATTR, 'true');
    target.hidden = true;
  }

  function showCard(card) {
    var target = getHideTarget(card);
    if (!target.hasAttribute(HIDDEN_ATTR)) return;

    target.removeAttribute(HIDDEN_ATTR);
    target.hidden = false;
  }

  function productHasStock(product) {
    var variants = product && Array.isArray(product.variants) ? product.variants : [];

    // Sin variantes en la respuesta no se asume agotado (mismo criterio que
    // sucursal-card-stock.js).
    if (!variants.length) return true;

    return variants.some(function (variant) {
      return Number(variant.available || 0) > 0;
    });
  }

  // Si tras filtrar no queda ningun producto, el encabezado "Productos" y el
  // carrusel vacio quedarian colgando sin nada debajo.
  function updateEmptyState(container) {
    var visibles = Array.prototype.filter.call(
      container.querySelectorAll(CARD_SELECTOR),
      function (card) {
        return !getHideTarget(card).hasAttribute(HIDDEN_ATTR);
      }
    ).length;

    var block = container.closest('.predictive-search-result--products');
    if (!block) return;

    block.hidden = visibles === 0;
  }

  function applyAvailability(cards, byProductId, byHandle) {
    cards.forEach(function (card) {
      if (!card.isConnected) return;

      var productId = normalizeId(card.getAttribute('data-product-id'));
      var link = card.querySelector('a[href*="/products/"]');
      var handle = '';

      if (link) {
        var match = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
        if (match) handle = match[1];
      }

      var product = byProductId[productId] || byHandle[handle];

      // El endpoint no lo devolvio: se deja visible antes que ocultar de mas.
      if (!product) {
        showCard(card);
        return;
      }

      if (productHasStock(product)) {
        showCard(card);
      } else {
        hideCard(card);
      }
    });

    Array.prototype.forEach.call(document.querySelectorAll(RESULTS_SELECTOR), updateEmptyState);
  }

  function indexProducts(products) {
    var byProductId = {};
    var byHandle = {};

    products.forEach(function (product) {
      if (!product) return;

      var id = normalizeId(product.productId || product.product_id || product.id || '');
      var handle = product.handle || product.productHandle || '';

      if (id) byProductId[id] = product;
      if (handle) byHandle[handle] = product;
    });

    return { byProductId: byProductId, byHandle: byHandle };
  }

  function refresh() {
    var state = getState();
    if (!state || !state.id) return Promise.resolve();

    var cards = getResultCards();
    if (!cards.length) return Promise.resolve();

    var locationId = String(state.id);

    // Cambio de sucursal invalida lo cacheado.
    if (cache.locationId !== locationId) {
      cache = { locationId: locationId, products: {} };
    }

    var ids = [];
    cards.forEach(function (card) {
      var id = normalizeId(card.getAttribute('data-product-id'));
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });

    if (!ids.length) return Promise.resolve();

    var pending = ids.filter(function (id) {
      return !Object.prototype.hasOwnProperty.call(cache.products, id);
    });

    // Todo cacheado: se aplica sin tocar la red.
    if (!pending.length) {
      var cachedList = ids.map(function (id) { return cache.products[id]; });
      var cachedIndex = indexProducts(cachedList.filter(Boolean));
      applyAvailability(cards, cachedIndex.byProductId, cachedIndex.byHandle);
      return Promise.resolve();
    }

    var thisRequest = ++requestCounter;

    return fetch(
      ENDPOINT +
        '?locationId=' + encodeURIComponent(locationId) +
        '&productIds=' + encodeURIComponent(pending.join(','))
    )
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (products) {
        if (thisRequest !== requestCounter) return;
        if (!Array.isArray(products)) return;

        products.forEach(function (product) {
          var id = normalizeId(
            (product && (product.productId || product.product_id || product.id)) || ''
          );
          if (id) cache.products[id] = product;
        });

        // Los que el endpoint no devolvio se marcan como consultados para no
        // volver a pedirlos en cada tecla.
        pending.forEach(function (id) {
          if (!Object.prototype.hasOwnProperty.call(cache.products, id)) {
            cache.products[id] = null;
          }
        });

        var all = ids
          .map(function (id) { return cache.products[id]; })
          .filter(Boolean);

        var index = indexProducts(all);
        applyAvailability(getResultCards(), index.byProductId, index.byHandle);
      })
      .catch(function (error) {
        console.warn('[sucursal-search-filter] No se pudo filtrar por sucursal.', error);
      });
  }

  function scheduleRefresh(delay) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, typeof delay === 'number' ? delay : 80);
  }

  // El HTML del predictivo se inyecta con innerHTML y no emite ningun evento,
  // asi que se observa la aparicion de cards dentro del contenedor de resultados.
  var observer = new MutationObserver(function (mutations) {
    var touched = mutations.some(function (mutation) {
      var target = mutation.target;

      if (target && target.closest && target.closest(RESULTS_SELECTOR)) return true;

      return Array.prototype.some.call(mutation.addedNodes || [], function (node) {
        if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;

        return Boolean(
          (node.matches && node.matches(RESULTS_SELECTOR)) ||
          (node.querySelector && node.querySelector(RESULTS_SELECTOR)) ||
          (node.closest && node.closest(RESULTS_SELECTOR))
        );
      });
    });

    if (touched) scheduleRefresh(60);
  });

  function start() {
    if (!document.body) return;
    observer.observe(document.body, { childList: true, subtree: true });
    scheduleRefresh(120);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('sucursal:changed', function () {
    cache = { locationId: '', products: {} };
    scheduleRefresh(60);
  });

  window.addEventListener('branch:changed', function () {
    cache = { locationId: '', products: {} };
    scheduleRefresh(60);
  });

  window.SucursalSearchFilter = { refresh: refresh };
})();
